'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function ServiceProcess({
  processes,
  service,
  title,
}: {
  processes: TDynamicData[];
  service: Service;
  title: string;
}) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'pb-16 pt-8 sm:pb-24 sm:pt-12 md:pb-32 md:pt-16 lg:pb-36 lg:pt-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative flex flex-col items-start justify-start gap-8 md:flex-row md:items-start md:justify-between',
        )}
      >
        <div className="relative flex w-full max-w-[486px] flex-col gap-12">
          <Heading
            subtitle={t('text3596')}
            title={title}
            className={{ container: 'w-full max-w-[440px]', title: 'text-balance' }}
          />
          <div className="relative flex h-full w-full flex-col items-start justify-start gap-4">
            {processes.map((process, index) => (
              <ProcessStep key={process.name} process={process} index={index} />
            ))}
          </div>
          <div
            className={cn(
              'absolute bottom-0 left-0 z-[10] h-[100px] w-full cursor-context-menu',
              'ea-service-process__bottom-overlay',
            )}
          />
        </div>
        <Image
          src={service?.images?.[1]?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(service?.images?.[1], t('text3596'))}
          width={0}
          height={0}
          className="sticky top-6 hidden aspect-square w-full max-w-[588px] object-cover object-center sm:block"
          sizes="100vw"
        />
      </div>
    </section>
  );
}

function ProcessStep({ process, index }: { process: TDynamicData; index: number }) {
  const processFields = process.fields ?? [];

  return (
    <div className={cn('relative z-[5] flex w-full items-stretch justify-start gap-6')}>
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] items-center justify-center bg-[#C04F1C]">
          <p className="text-lg font-bold tracking-tighter text-white">0{index + 1}</p>
        </div>
        <div className="h-full w-[1px] bg-primary-foreground" />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <h3 data-ea-visual-tag="h5" className="text-2xl font-bold tracking-tighter">{process.name}</h3>
        <HtmlRendererRaw
          className={cn('w-full pb-8', 'ea-process-step__description')}
          html={processFields[1]?.value ?? ''}
        />
      </div>
    </div>
  );
}
